import type { LatLngString, PlaceSearchResult } from '../arcticapi';

/**
 * Option model for place autocomplete datalists.
 *
 * `key` is only a list-item identity for rendering, not a business ID -
 * resolve the selected place via `place`, which `toPlaceOptions` always sets.
 */
export interface PlaceOption {
  key: string;
  value: string;
  place?: PlaceSearchResult;
  primaryText?: string;
  label?: string;
  distance?: string;
  type?: PlaceSearchResult['type'];
  indicator?: string;
  localityName?: string | null;
}

/** Renders a place result into a user-facing autocomplete label. */
export function formatPlaceOption(place: PlaceSearchResult): string {
  return place.localityName
    ? `${place.commonName} (${place.localityName})`
    : place.commonName;
}

export function formatPlaceDistance(distanceMeters: number): string {
  const roundedKilometres = Math.max(
    0.1,
    Math.round((distanceMeters / 1000) * 10) / 10,
  );

  if (roundedKilometres >= 10 || Number.isInteger(roundedKilometres)) {
    return `${Math.round(roundedKilometres)}km`;
  }

  return `${roundedKilometres.toFixed(1).replace(/\.0$/u, '')}km`;
}

/**
 * Resolves a text input value to an exact place search result from the latest
 * search response.
 */
export function findPlaceByQuery(
  query: string,
  places: PlaceSearchResult[],
): PlaceSearchResult | null {
  const trimmed = query.trim();
  if (!trimmed) return null;
  const lowered = trimmed.toLowerCase();
  return (
    places.find((place) => {
      const formatted = formatPlaceOption(place).toLowerCase();
      return (
        formatted === lowered ||
        place.commonName.toLowerCase() === lowered ||
        place.id.toLowerCase() === lowered
      );
    }) ?? null
  );
}

/** Converts place-search matches into datalist options. */
export function toPlaceOptions(places: PlaceSearchResult[]): PlaceOption[] {
  return places.map((place) => ({
    key: place.id,
    primaryText: place.commonName,
    label: place.localityName ?? undefined,
    distance: toOptionDistance(place),
    type: place.type,
    indicator: place.indicator,
    localityName: place.localityName,
    value: formatPlaceOption(place),
    place,
  }));
}

/** Converts a place result's GeoJSON point to planner `lat,lng` format. */
export function placeToLatLng(place: PlaceSearchResult): LatLngString {
  const [lng, lat] = place.location.coordinates;
  return `${lat},${lng}`;
}

function toOptionDistance(place: PlaceSearchResult): string | undefined {
  const distanceMeters = place.distance;

  if (typeof distanceMeters !== 'number' || !Number.isFinite(distanceMeters)) {
    return undefined;
  }

  return formatPlaceDistance(distanceMeters);
}
